import { useEffect, useState } from 'react';
import { useAuth } from '../../auth-lib';
import { apiClient } from '../../auth-lib';
import { AnimatePresence, motion } from 'framer-motion';
// Employee shell (orgadmin variant) — this page is reached from the employee
// area nav, so it must share that chrome rather than the old emoji UserLayout.
import EmployeeLayout from '../components/EmployeeLayout';
import { Button, Card, StatusPill, Tabs, EmptyState, Skeleton, useToast } from '../../design-system';
import '../styles/ComplianceWorkspace.css';

const WORKFLOW_COLUMNS = [
  { key: 'todo', label: 'To Do' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'in_review', label: 'In Review' },
  { key: 'changes_requested', label: 'Changes Requested' },
  { key: 'done', label: 'Done' },
];

const NEXT_STATE = {
  todo: 'in_progress',
  in_progress: 'in_review',
  changes_requested: 'in_progress',
};

function ComplianceWorkspace() {
  const { user } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState('prepare');
  const [preparedTasks, setPreparedTasks] = useState([]);
  const [reviewTasks, setReviewTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?._id) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [prepRes, reviewRes] = await Promise.all([
        apiClient.get('/api/tasks', { params: { preparerId: user._id, approvalStatus: 'approved' } }),
        apiClient.get('/api/tasks', { params: { reviewerId: user._id, workflowStatus: 'in_review' } }),
      ]);
      setPreparedTasks(prepRes.data);
      setReviewTasks(reviewRes.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load compliance tasks');
    } finally {
      setLoading(false);
    }
  };

  const nextStateFor = (task) => {
    if (task.workflowStatus === 'in_progress' && !task.reviewerId) return 'done';
    return NEXT_STATE[task.workflowStatus];
  };

  const advanceTask = async (task) => {
    const next = nextStateFor(task);
    if (!next) return;
    try {
      const res = await apiClient.patch(`/api/tasks/${task._id}/status`, { workflowStatus: next });
      setPreparedTasks((prev) => prev.map((t) => (t._id === task._id ? res.data : t)));
      toast.success(`Moved to ${WORKFLOW_COLUMNS.find((c) => c.key === next)?.label}`);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update task');
    }
  };

  const reviewDecision = async (task, decision) => {
    try {
      await apiClient.patch(`/api/tasks/${task._id}/status`, { workflowStatus: decision });
      setReviewTasks((prev) => prev.filter((t) => t._id !== task._id));
      toast.success(decision === 'done' ? 'Signed off' : 'Changes requested');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to submit review decision');
    }
  };

  const isOverdue = (task) => task.dueDate && new Date(task.dueDate) < new Date() && task.workflowStatus !== 'done';

  const tabs = [
    { key: 'prepare', label: 'My Prepared Tasks', count: preparedTasks.length },
    { key: 'review', label: 'My Reviews', count: reviewTasks.length },
  ];

  if (loading) {
    return (
      <EmployeeLayout title="Compliance Workspace" subtitle="Your prepared tasks and pending reviews">
        <Card><Skeleton.Text lines={4} /></Card>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout title="Compliance Workspace" subtitle="Your prepared tasks and pending reviews">
      <div className="compliance-workspace">
        <Tabs tabs={tabs} active={tab} onChange={setTab} />

        {tab === 'prepare' && (
          <div className="kanban-board">
            {WORKFLOW_COLUMNS.map((col) => {
              const colTasks = preparedTasks.filter((t) => t.workflowStatus === col.key);
              return (
                <div key={col.key} className="kanban-column">
                  <div className="kanban-column-header">
                    {col.label}
                    <span className="kanban-count">{colTasks.length}</span>
                  </div>
                  <div className="kanban-cards">
                    <AnimatePresence>
                      {colTasks.map((task) => (
                        <motion.div
                          key={task._id}
                          layout
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.96 }}
                          transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                        >
                          <Card padding="sm" className={`kanban-card ${isOverdue(task) ? 'overdue' : ''}`}>
                            <div className="kanban-card-title">{task.title}</div>
                            {task.clientId?.displayName && (
                              <div className="kanban-card-client">{task.clientId.displayName}</div>
                            )}
                            <div className="kanban-card-meta">
                              <StatusPill status={task.priority} />
                              {task.dueDate && (
                                <span className={isOverdue(task) ? 'overdue-text' : 'meta-text'}>
                                  {new Date(task.dueDate).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            {nextStateFor(task) && (
                              <Button
                                variant="secondary"
                                size="sm"
                                className="btn-advance"
                                onClick={() => advanceTask(task)}
                              >
                                Move to {WORKFLOW_COLUMNS.find((c) => c.key === nextStateFor(task))?.label}
                              </Button>
                            )}
                          </Card>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    {colTasks.length === 0 && <p className="kanban-empty">No tasks</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === 'review' && (
          <div className="review-list">
            {reviewTasks.length === 0 ? (
              <EmptyState icon="🔍" title="Nothing to review" description="No tasks are awaiting your review right now." />
            ) : (
              <AnimatePresence>
                {reviewTasks.map((task) => (
                  <motion.div
                    key={task._id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <Card className="review-card">
                      <div className="review-info">
                        <strong>{task.title}</strong>
                        {task.clientId?.displayName && (
                          <span className="review-client">{task.clientId.displayName}</span>
                        )}
                        {task.preparerId?.name && (
                          <span className="meta-text">Prepared by: {task.preparerId.name}</span>
                        )}
                      </div>
                      <div className="review-actions">
                        <Button variant="primary" size="sm" onClick={() => reviewDecision(task, 'done')}>
                          Sign Off
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => reviewDecision(task, 'changes_requested')}
                        >
                          Request Changes
                        </Button>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        )}
      </div>
    </EmployeeLayout>
  );
}

export default ComplianceWorkspace;
